import { forwardRef } from "react"

import { Box, type BoxProps } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { find } from "lodash"

import { Image, type FC } from ".."

export type ImageVariant = {
  url: string // Url to the image
  title: string // File name + ext of the image
  style: string // Responsive style of the image (teaser, hero, etc)
  alt: string // Alt text for the image
  height: number | null // Height of the image
  width: number | null // Width of the image
}

export type ResponsiveImageType = {
  variants: ImageVariant[]
}

export type ResponsiveImageProps = {
  pictureProps?: BoxProps<"picture">
  imgStyle?: string
  image?: ResponsiveImageType
} & BoxProps<"img">

export const ResponsiveImage = forwardRef<HTMLImageElement, ResponsiveImageProps>(
  ({ imgStyle, image, sx, pictureProps, ...props }, ref) => {
    const theme = useTheme()

    if (!image || !image.variants) return <ErrorImage {...props} />

    // get the default image
    const imgDefault =
      find(image.variants, ["style", "default"]) ??
      find(image.variants, ["style", imgStyle ? `${imgStyle}_3xl` : "3xl"]) ??
      null

    // no default image, render an error
    if (!imgDefault || !imgDefault.url) return <ErrorImage sx={sx} {...props} />

    const sources: { srcSet: string; media: string }[] = []
    const breakpoints = theme.breakpoints.keys

    for (let i = breakpoints.length - 1; i > 0; i--) {
      const bp = breakpoints[i]
      const nextBp = breakpoints[i - 1]

      // if the image style exists, find only the variants that match
      const variant = find(image.variants, ["style", imgStyle ? `${imgStyle}_${bp}` : bp])
      if (variant) {
        sources.push({
          srcSet: `${variant.url} 1x`,
          media: `(min-width: ${theme.breakpoints.values[nextBp]}px)`,
        })
      }
    }

    return (
      <Box component="picture" {...pictureProps}>
        {sources.length > 0 &&
          sources.map(({ srcSet, media }, i) => <source key={i} srcSet={srcSet} media={media} />)}
        <Image
          ref={ref}
          src={imgDefault.url}
          title={imgDefault.title}
          alt={imgDefault.alt}
          loading="lazy"
          sx={[
            { display: "block", width: "100%", height: "100%", objectFit: "cover" },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          {...props}
        />
      </Box>
    )
  }
)
ResponsiveImage.displayName = "ResponsiveImage"

const ErrorImage: FC<BoxProps> = ({ sx, ...props }) => (
  <Box
    alignItems="center"
    justifyContent="center"
    bgcolor={(theme) => alpha(theme.palette["neutral.900"].main, 0.15)}
    p={5}
    sx={[
      ...(Array.isArray(sx) ? sx : [sx]),
      {
        display: "flex",
      },
    ]}
    {...props}
  />
)

export const generateImageVariants = (width: number, height: number): ImageVariant[] => {
  const variants: ImageVariant[] = []

  const sizes = [
    { style: "default", width: 0, height: 0 },
    { style: "teaser", width: 750, height: 700 },
    { style: "hero", width: 1920, height: 1080 },
  ]

  sizes.forEach((size) => {
    variants.push({
      url: `https://picsum.photos/${size.width}/${size.height}?random=${Math.floor(
        Math.random() * 1000
      )}`,
      title: `image_${size.width}x${size.height}.jpg`,
      style: size.style,
      alt: `image_${size.width}x${size.height}.jpg`,
      height: size.height,
      width: size.width,
    })
  })

  return variants
}
